import React, { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Loader2, QrCode, Download, Check, X, ChevronDown, ChevronRight, Copy, Package } from 'lucide-react';

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }) : '');

const blobError = async (err) => {
  let detail = err.response?.data?.detail;
  if (err.response?.data instanceof Blob) {
    try { detail = JSON.parse(await err.response.data.text())?.detail; } catch { detail = ''; }
  }
  return detail;
};

function NameList({ names, testId }) {
  const [openList, setOpenList] = useState(false);
  if (!names?.length) return null;
  const copy = () => {
    navigator.clipboard?.writeText(names.map((n, i) => `${i + 1}. ${n}`).join('\n'))
      .then(() => toast.success('Namensliste kopiert')).catch(() => toast.error('Kopieren fehlgeschlagen'));
  };
  return (
    <div className="space-y-1" data-testid={testId}>
      <div className="flex items-center gap-2 flex-wrap">
        <button onClick={() => setOpenList(o => !o)} data-testid={`${testId}-toggle`}
          className="flex items-center gap-1 text-[10px] text-foreground/80 hover:text-foreground transition-colors">
          {openList ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
          <span className="font-mono">{names[0]}</span>
          {names.length > 1 && <span className="text-muted-foreground">… <span className="font-mono text-foreground/80">{names[names.length - 1]}</span></span>}
          <span className="text-muted-foreground">({names.length} Namen {openList ? 'ausblenden' : 'anzeigen'})</span>
        </button>
        <button onClick={copy} data-testid={`${testId}-copy`} title="Namensliste in Reihenfolge kopieren"
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
          <Copy className="w-3 h-3" /> Liste kopieren
        </button>
      </div>
      {openList && (
        <ol className="max-h-40 overflow-y-auto border border-border/60 rounded-sm px-2 py-1 text-[10px] font-mono text-foreground/90 columns-2 gap-3">
          {names.map((n, i) => <li key={i} className="truncate"><span className="text-muted-foreground">{i + 1}.</span> {n}</li>)}
        </ol>
      )}
    </div>
  );
}

function PackageCard({ pkg, busy, onDownload, onConsume, onDissolve }) {
  const key = pkg.id;
  const done = pkg.count - (pkg.still_open ?? pkg.count);
  return (
    <div className="border border-sky-500/40 bg-sky-500/5 rounded-sm p-2 space-y-1.5" data-testid={`qr-package-${pkg.seq}`}>
      <div className="flex items-center gap-1.5 flex-wrap">
        <Package className="w-3.5 h-3.5 text-sky-300" />
        <span className="text-xs font-medium text-foreground" data-testid={`qr-package-title-${pkg.seq}`}>Paket #{pkg.seq}</span>
        <span className="text-[10px] font-mono px-1.5 py-px rounded-full border text-sky-300 border-sky-500/40 bg-sky-500/10">{pkg.count} QR-Codes</span>
        <span className="text-[10px] text-muted-foreground">gebildet {fmtDate(pkg.created_at)}{pkg.created_by ? ` von ${pkg.created_by}` : ''}</span>
        {pkg.downloads > 0
          ? <span className="text-[10px] text-emerald-300/90 font-mono" data-testid={`qr-package-downloads-${pkg.seq}`}>PDF {pkg.downloads}× geladen · zuletzt {fmtDate(pkg.last_download_at)}</span>
          : <span className="text-[10px] text-amber-300/90 font-mono" data-testid={`qr-package-downloads-${pkg.seq}`}>noch nicht geladen</span>}
        {done > 0 && <span className="text-[10px] text-muted-foreground font-mono">{done} inzwischen anders erledigt</span>}
        <button onClick={() => onDissolve(pkg)} disabled={busy === `dissolve-${key}`} data-testid={`qr-package-dissolve-${pkg.seq}`}
          className="ml-auto p-0.5 text-muted-foreground hover:text-red-400 transition-colors" title="Paket auflösen — QR-Codes werden wieder frei">
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <NameList names={pkg.names} testId={`qr-package-names-${pkg.seq}`} />
      <div className="flex items-center gap-2 flex-wrap">
        <Button size="sm" onClick={() => onDownload(pkg)} disabled={busy === `pdf-${key}`} data-testid={`qr-package-download-${pkg.seq}`}
          className="h-6 text-[10px] rounded-sm bg-sky-600 hover:bg-sky-500 text-white">
          {busy === `pdf-${key}` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Download className="w-3 h-3 mr-1" />}
          QR-PDF laden ({pkg.count} Seiten)
        </Button>
        <Button size="sm" variant="outline" onClick={() => onConsume(pkg)} disabled={busy === `consume-${key}`} data-testid={`qr-package-consume-${pkg.seq}`}
          className="h-6 text-[10px] rounded-sm border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 disabled:opacity-50">
          {busy === `consume-${key}` ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Check className="w-3 h-3 mr-1" />}
          Gedruckt → {pkg.still_open ?? pkg.count} Flyer abbuchen
        </Button>
      </div>
    </div>
  );
}

// Step "QR-Codes": open QR codes are frozen into numbered packages of ≤50 (alphabetical). The names of
// every package stay visible, so a printed stack can always be matched to its letters.
export function FlyerQrPackages({ open, beforeDownload, onChanged }) {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState('');
  const [showRecent, setShowRecent] = useState(false);

  const load = () => api.getFlyerQrPackages().then(r => setData(r.data)).catch(() => setData(d => d || { pending: [], next: [], recent: [], no_landing: [] }));
  useEffect(() => { if (open) load(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  const act = async (key, fn, okMsg) => {
    setBusy(key);
    try {
      const r = await fn();
      if (okMsg) toast.success(okMsg(r), { duration: 8000 });
      await load();
      onChanged?.();
    } catch (err) { toast.error((await blobError(err)) || 'Fehlgeschlagen', { duration: 10000 }); }
    finally { setBusy(''); }
  };

  const download = async (pkg) => {
    setBusy(`pdf-${pkg.id}`);
    try {
      if (beforeDownload && !(await beforeDownload())) return;
      const res = await api.downloadFlyerQrPackagePdf(pkg.id);
      const n = res.headers?.['x-flyer-count'] || pkg.count;
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url; a.download = `OLTA_QR_Paket${String(pkg.seq).padStart(3, '0')}_${n}x.pdf`; a.click();
      URL.revokeObjectURL(url);
      toast.success(`Paket #${pkg.seq}: ${n} QR-Seiten geladen — einseitig auf ${n} Vorrats-Flyer drucken (Reihenfolge = Namensliste), danach „abbuchen“`, { duration: 10000 });
      await load();
    } catch (err) { toast.error((await blobError(err)) || 'Download fehlgeschlagen', { duration: 10000 }); }
    finally { setBusy(''); }
  };

  const consume = (pkg) => {
    const n = pkg.still_open ?? pkg.count;
    if (!window.confirm(`Paket #${pkg.seq}: ${n} QR-Codes sind auf Vorrats-Flyer gedruckt?\n\nDie Flyer dieser ${n} Briefe gelten dann als fertig, der Vorrat sinkt um ${n}${pkg.downloads ? '' : ' (PDF wurde noch nicht geladen!)'}.`)) return;
    act(`consume-${pkg.id}`, () => api.consumeFlyerQrPackage(pkg.id),
      r => `Paket #${r.data.package_seq}: ${r.data.consumed} Flyer abgebucht — noch ${r.data.available} im Vorrat`);
  };
  const dissolve = (pkg) => {
    if (!window.confirm(`Paket #${pkg.seq} auflösen? Die ${pkg.count} QR-Codes werden wieder frei und landen im nächsten Paket.`)) return;
    act(`dissolve-${pkg.id}`, () => api.dissolveFlyerQrPackage(pkg.id), r => `Paket #${r.data.package_seq} aufgelöst`);
  };
  const create = () => act('create', () => api.createFlyerQrPackage(),
    r => `Paket #${r.data.package.seq} mit ${r.data.package.count} QR-Codes gebildet — Namen prüfen, PDF laden`);

  if (!data) return <div className="flex items-center gap-1 text-[10px] text-muted-foreground" data-testid="qr-packages-loading"><Loader2 className="w-3 h-3 animate-spin" /> QR-Pakete laden…</div>;
  const pending = data.pending || [];
  const next = data.next || [];
  const size = data.package_size || 50;
  const short = (data.available || 0) < pending.reduce((s, p) => s + (p.still_open ?? p.count), 0);

  return (
    <div className="space-y-2" data-testid="qr-packages">
      <div className="flex items-center gap-2 flex-wrap text-[10px] font-mono">
        <span className="px-2 py-0.5 rounded-full border text-sky-300 border-sky-500/40 bg-sky-500/10" data-testid="qr-packages-stock">
          Vorrat: {data.available ?? 0} Flyer fertig{data.in_progress ? ` · ${data.in_progress} in Arbeit` : ''}
        </span>
        <span className="text-muted-foreground" data-testid="qr-packages-open">{data.open_qr ?? 0} QR-Codes offen · {pending.length} Paket{pending.length !== 1 ? 'e' : ''} gebildet</span>
      </div>
      {short && (
        <p className="text-[10px] text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-sm px-2 py-1" data-testid="qr-packages-short">
          Vorrat reicht nicht für alle gebildeten Pakete — erst Vorrats-Flyer nachschreiben, sonst schlägt das Abbuchen fehl.
        </p>
      )}
      {pending.map(p => <PackageCard key={p.id} pkg={p} busy={busy} onDownload={download} onConsume={consume} onDissolve={dissolve} />)}
      {next.length > 0 ? (
        <div className="border border-dashed border-border rounded-sm p-2 space-y-1.5" data-testid="qr-package-next">
          <div className="flex items-center gap-1.5 flex-wrap">
            <QrCode className="w-3.5 h-3.5 text-muted-foreground" />
            <span className="text-xs font-medium text-foreground">Nächstes Paket</span>
            <span className="text-[10px] text-muted-foreground font-mono" data-testid="qr-package-next-count">
              {next.length} von {data.free_total} freien QR-Codes{data.free_total > size ? ` · ${data.free_total - size} weitere folgen im nächsten Paket` : ''}
            </span>
          </div>
          <NameList names={next.map(l => l.name)} testId="qr-package-next-names" />
          <Button size="sm" variant="outline" onClick={create} disabled={busy === 'create'} data-testid="qr-package-create-btn"
            className="h-6 text-[10px] rounded-sm border-sky-500/40 text-sky-300 hover:bg-sky-500/10 disabled:opacity-50">
            {busy === 'create' ? <Loader2 className="w-3 h-3 mr-1 animate-spin" /> : <Package className="w-3 h-3 mr-1" />}
            Paket bilden ({next.length} QR-Codes)
          </Button>
        </div>
      ) : pending.length === 0 && (
        <p className="text-[10px] text-emerald-300/90" data-testid="qr-packages-empty">Alle QR-Codes abgebucht — nichts offen.</p>
      )}
      {data.no_landing?.length > 0 && (
        <p className="text-[10px] text-amber-300 border border-amber-500/30 bg-amber-500/10 rounded-sm px-2 py-1" data-testid="qr-packages-no-landing">
          {data.no_landing.length} Brief{data.no_landing.length !== 1 ? 'e' : ''} ohne Landing Page (kein QR möglich): {data.no_landing.slice(0, 4).join(', ')}{data.no_landing.length > 4 ? ' …' : ''}
        </p>
      )}
      {data.recent?.length > 0 && (
        <div className="space-y-1">
          <button onClick={() => setShowRecent(s => !s)} data-testid="qr-packages-recent-toggle"
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground transition-colors">
            {showRecent ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
            Zuletzt abgebucht ({data.recent.length})
          </button>
          {showRecent && data.recent.map(p => (
            <div key={p.id} className="border border-border/60 rounded-sm px-2 py-1 space-y-1 opacity-80" data-testid={`qr-package-recent-${p.seq}`}>
              <div className="text-[10px] font-mono text-muted-foreground">
                Paket #{p.seq} · {p.consumed_count ?? p.count} Flyer abgebucht {fmtDate(p.consumed_at)}{p.consumed_by ? ` von ${p.consumed_by}` : ''}
              </div>
              <NameList names={p.names} testId={`qr-package-recent-names-${p.seq}`} />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
